import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, MapPin, Building2, ShieldCheck, Sparkles,
  Scale, DollarSign, HelpCircle,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import SEOTrustBlock from "@/components/SEOTrustBlock";
import GovResourcesInline from "@/components/landing/GovResourcesInline";
import { PROVINCE_SEO_DATA, humaniseCity } from "@/data/province-seo";

/**
 * PropertyManagementProvince — SEO landing page that ranks for the
 * "property management company in <province>" query in every Canadian
 * province + territory. Each page carries:
 *   - LocalBusiness + Service JSON-LD scoped to the province.
 *   - Open Graph + Twitter Card meta for share-preview platforms.
 *   - Direct signup CTAs UTM-tagged so paid ad attribution is clean.
 *
 * 13 provinces/territories share this single component — config below.
 */
const PROVINCE_CONFIG = {
  "ontario":                   { code: "ON", name: "Ontario",                   tagline: "From Toronto to Thunder Bay — RTA-compliant, LTB-ready." },
  "british-columbia":          { code: "BC", name: "British Columbia",          tagline: "Vancouver, Victoria, the Interior — RTB-ready property management." },
  "alberta":                   { code: "AB", name: "Alberta",                   tagline: "Calgary, Edmonton + the rest — RTDRS-friendly." },
  "quebec":                    { code: "QC", name: "Quebec",                    tagline: "Montréal, Québec, partout — bilingue et conforme au TAL." },
  "manitoba":                  { code: "MB", name: "Manitoba",                  tagline: "Winnipeg, Brandon, Steinbach — RTB-compliant." },
  "saskatchewan":              { code: "SK", name: "Saskatchewan",              tagline: "Regina, Saskatoon and beyond — ORT-ready." },
  "nova-scotia":               { code: "NS", name: "Nova Scotia",               tagline: "Halifax, Cape Breton — RTO-ready property management." },
  "new-brunswick":             { code: "NB", name: "New Brunswick",             tagline: "Fredericton, Saint John, Moncton — RTT-compliant." },
  "newfoundland-and-labrador": { code: "NL", name: "Newfoundland and Labrador", tagline: "St. John's, Mount Pearl — RTO-ready." },
  "prince-edward-island":      { code: "PE", name: "Prince Edward Island",      tagline: "Charlottetown, Summerside — IRAC-compliant." },
  "yukon":                     { code: "YT", name: "Yukon",                     tagline: "Whitehorse and the territories — RLO-ready." },
  "northwest-territories":     { code: "NT", name: "Northwest Territories",     tagline: "Yellowknife, Inuvik — RTO-compliant." },
  "nunavut":                   { code: "NU", name: "Nunavut",                   tagline: "Iqaluit and the territory — RTA-ready." },
};

const SERVICE_PILLARS = [
  { icon: Building2,   title: "Full-service tenant placement", body: "Listing the unit, screening applicants and signing a province-compliant lease — usually within 21 days." },
  { icon: ShieldCheck, title: "Rent collection + arrears",     body: "Stripe-powered CAD rent collection with auto-receipts and arrears follow-up. Owner statements every month." },
  { icon: Sparkles,    title: "Smart maintenance triage",      body: "Tenants submit a photo ticket; the system grades severity, drafts the reply and dispatches a vendor." },
  { icon: MapPin,      title: "Provincial compliance",         body: "Lease forms, notices, inspections and entry rules — calibrated to provincial law, kept current as it changes." },
];


export default function PropertyManagementProvince() {
  const { province: provSlug } = useParams();
  const cfg = PROVINCE_CONFIG[provSlug];
  // Per-province SEO seed: cities, board, deposit rules, FAQ, avg rent, etc.
  // Falls back to empty when a slug is added to PROVINCE_CONFIG but not yet
  // seeded — the page still renders, it just lacks the rich local blocks.
  const seed = PROVINCE_SEO_DATA[provSlug] || {};

  useEffect(() => {
    if (!cfg) return;
    const url = `https://centralrentals.ca/property-management/${provSlug}`;
    const title = `Property management company in ${cfg.name} · Central Rentals Canada`;
    const desc = `${cfg.tagline} Central Rentals is a tech-first property management company serving ${cfg.name} — full-service tenant placement, rent collection, maintenance and ${cfg.name} compliance.`;
    document.title = title;
    const setTag = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const tags = [
      setTag('meta[name="description"]',           { name: "description", content: desc }),
      setTag('meta[name="keywords"]',              { name: "keywords",
        content: `property management company ${cfg.name}, property management ${cfg.name}, ${cfg.name} property managers, tenant placement ${cfg.name}, rent collection ${cfg.name}` }),
      setTag('link[rel="canonical"]',              { rel: "canonical", href: url }),
      setTag('meta[property="og:type"]',           { property: "og:type", content: "website" }),
      setTag('meta[property="og:title"]',          { property: "og:title", content: title }),
      setTag('meta[property="og:description"]',    { property: "og:description", content: desc }),
      setTag('meta[property="og:url"]',            { property: "og:url", content: url }),
      setTag('meta[property="og:image"]',          { property: "og:image", content: "https://centralrentals.ca/og-image.png" }),
      setTag('meta[name="twitter:card"]',          { name: "twitter:card", content: "summary_large_image" }),
      setTag('meta[name="twitter:title"]',         { name: "twitter:title", content: title }),
      setTag('meta[name="twitter:description"]',   { name: "twitter:description", content: desc }),
    ];
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
          "@id": `${url}#business`,
          "name": `Central Rentals Canada — ${cfg.name}`,
          "url": url,
          "logo": "https://centralrentals.ca/icon-512.png",
          "image": "https://centralrentals.ca/og-image.png",
          "description": desc,
          "priceRange": "$",
          "areaServed": { "@type": "AdministrativeArea", "name": cfg.name, "addressCountry": "CA" },
          "address": { "@type": "PostalAddress", "addressRegion": cfg.name, "addressCountry": "CA" },
          "telephone": "+1-587-803-0014",
          "sameAs": [
            "https://www.facebook.com/centralrentalscanada",
            "https://www.linkedin.com/company/central-rentals-canada",
          ],
        },
        {
          "@type": "Service",
          "name": `Property management services in ${cfg.name}`,
          "serviceType": "Property management",
          "provider": { "@type": "Organization", "name": "Central Rentals Canada", "url": "https://centralrentals.ca/" },
          "areaServed": { "@type": "AdministrativeArea", "name": cfg.name, "addressCountry": "CA" },
          "description": desc,
        },
        // Province-specific FAQ schema — dramatically improves AI Overview
        // + rich-snippet coverage vs. a generic FAQ.
        ...(seed.faq && seed.faq.length ? [{
          "@type": "FAQPage",
          "mainEntity": seed.faq.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }] : []),
      ],
    };
    let script = document.head.querySelector('script[data-pmprov-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-pmprov-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => {
      tags.forEach((el) => el && el.remove());
      if (script) script.remove();
    };
  }, [cfg, provSlug, seed]);

  if (!cfg) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`pmprov-${provSlug}`}>
      <PublicHeader light={true}/>
      <section className="relative border-b border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-emerald-200 text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-700 shadow-sm">
            <MapPin className="w-3 h-3"/> Property management · {cfg.name}
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-4 leading-[1.05] max-w-4xl text-slate-950">
            Property management company in {cfg.name}.
          </h1>
          <p className="text-base md:text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed">
            {cfg.tagline} Central Rentals is the tech-first property management company serving {cfg.name} — full-service tenant placement, rent collection, smart maintenance, and provincial compliance.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={`/signup?role=landlord&utm_source=seo&utm_medium=pm-province&utm_campaign=${cfg.code}`}
              data-testid={`pmprov-${provSlug}-cta`}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-md shadow-emerald-500/30 transition-colors">
              Get a {cfg.name} quote <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to={`/landlord-software/${provSlug}`} data-testid={`pmprov-${provSlug}-software`}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-300 hover:border-emerald-500 hover:text-emerald-700 text-slate-900 font-bold text-sm transition-colors">
              Or use the software yourself
            </Link>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> {cfg.name}-compliant leases</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Trust-account ready</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Made in Canada</span>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <SEOTrustBlock
          title={`Property management company in ${cfg.name}`}
          canonicalUrl={`https://centralrentals.ca/property-management/${provSlug}`}
          province={{ code: cfg.code, name: cfg.name, slug: provSlug }}
          kind="landing"
          readingMinutes={5}
        />
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tighter text-slate-900">
          What&apos;s included for every {cfg.name} owner.
        </h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {SERVICE_PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="p-6 rounded-2xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all"
                data-testid={`pmprov-${provSlug}-pillar-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <Icon className="w-5 h-5"/>
                </div>
                <h3 className="font-display text-lg font-bold mt-4 text-slate-950">{p.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 leading-tight">
            Talk to a {cfg.name} property manager.
          </h2>
          <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
            Tell us a bit about your portfolio — we&apos;ll come back with a quote and an onboarding plan for {cfg.name} usually within one business day.
          </p>
          <div className="mt-6 flex justify-center">
            <Link to={`/get-started?province=${cfg.code}&utm_source=seo&utm_medium=pm-province&utm_campaign=${cfg.code}-cta`}
              data-testid={`pmprov-${provSlug}-cta-footer`}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors">
              Get my quote <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </section>

      {/* PROVINCE-SEED SEO BLOCKS — hidden gracefully when data is missing.
          These sections add ~800-1200 words of unique, localised content per
          province so we can rank for hyper-local queries without duplicate-
          content penalties. Every anchor is an internal link for crawl equity. */}
      {seed.top_cities && seed.top_cities.length > 0 && (
        <section className="border-b border-slate-100 bg-white" data-testid={`pmprov-${provSlug}-cities`}>
          <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
              <MapPin className="w-3 h-3"/> Local city coverage
            </div>
            <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold tracking-tighter text-slate-900">
              Property management in every corner of {cfg.name}.
            </h2>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Central Rentals serves owner-operators across {cfg.name} — from single condos to 200-door portfolios. Popular city hubs:
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {seed.top_cities.map((c) => (
                <Link
                  key={c}
                  to={`/property-management-software/${c}`}
                  data-testid={`pmprov-${provSlug}-city-${c}`}
                  className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800 border border-slate-200 hover:border-emerald-300 text-slate-700 text-sm font-semibold transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5"/> {humaniseCity(c)}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {(seed.board || seed.avg_rent || seed.notice_days) && (
        <section className="bg-slate-50 border-b border-slate-100" data-testid={`pmprov-${provSlug}-stats`}>
          <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
            <div className="grid md:grid-cols-3 gap-4">
              {seed.board && (
                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <Scale className="w-5 h-5 text-emerald-700"/>
                  <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">Regulator</div>
                  <div className="mt-1 font-display text-lg font-bold text-slate-900 leading-snug">{seed.board}</div>
                </div>
              )}
              {seed.avg_rent && (
                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <DollarSign className="w-5 h-5 text-emerald-700"/>
                  <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">Avg 1BR rent (2026)</div>
                  <div className="mt-1 font-display text-lg font-bold text-slate-900 tabular-nums">
                    ${seed.avg_rent.toLocaleString("en-CA")} <span className="text-sm text-slate-500 font-normal">CAD / month</span>
                  </div>
                </div>
              )}
              {seed.notice_days && (
                <div className="p-6 rounded-2xl bg-white border border-slate-200">
                  <ShieldCheck className="w-5 h-5 text-emerald-700"/>
                  <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">Standard notice period</div>
                  <div className="mt-1 font-display text-lg font-bold text-slate-900 tabular-nums">{seed.notice_days} days</div>
                </div>
              )}
            </div>
            {seed.unique_hook && (
              <p className="mt-8 text-sm md:text-base text-slate-700 max-w-3xl leading-relaxed">{seed.unique_hook}</p>
            )}
          </div>
        </section>
      )}

      {seed.faq && seed.faq.length > 0 && (
        <section className="bg-white" data-testid={`pmprov-${provSlug}-faq`}>
          <div className="max-w-3xl mx-auto px-5 md:px-8 py-16">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
              <HelpCircle className="w-3 h-3"/> {cfg.name} landlord FAQ
            </div>
            <h2 className="mt-2 font-display text-2xl md:text-4xl font-bold tracking-tighter text-slate-900">
              Answers for {cfg.name} landlords.
            </h2>
            <div className="mt-8 space-y-6">
              {seed.faq.map((f, i) => (
                <div key={i} data-testid={`pmprov-${provSlug}-faq-${i}`}>
                  <h3 className="font-display text-lg font-bold text-slate-900">{f.q}</h3>
                  <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Government resources — every province page links to the
          national CMHC + provincial RTB + regulator + industry bodies
          so Central Rentals is discoverable alongside authoritative
          sources for property management in <province>. */}
      <GovResourcesInline
        variant="compact"
        title={`Official ${cfg.name} property management resources`}
        subtitle={`Central Rentals Canada — recommended property management software in ${cfg.name} — cross-references every rule against these official government sources.`}
      />
    </div>
  );
}
